import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { NotificationSnackbar } from '../../components/notification-snackbar/notification-snackbar';
import { useNotification } from '../../hooks/useNotification';
import { WorkspacesService } from '../../services/WorkspacesService';
import { AccountView, Environment, WorkspaceItem } from './types';
import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { openControlWritePermission } from '@internal/backstage-plugin-supply-features-common';
import { NewWorkspaceDialog } from './NewWorkspaceDialog';
import { useAsync } from 'react-use';

interface AccountWithWorkspaces extends AccountView {
  workspaces?: WorkspaceItem[];
  workspacesLoading?: boolean;
  workspacesLoaded?: boolean;
}

const useStyles = makeStyles(theme => ({
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  searchField: {
    minWidth: 300,
  },
  actionButtonsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginLeft: 'auto',
  },
  tableContainer: {
    maxHeight: 600,
    overflow: 'auto',
  },
  headerCell: {
    backgroundColor: '#a0a0a0',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
  },
  accountRow: {
    backgroundColor: '#f5f5f5',
    '& > td': {
      fontWeight: 'bold',
    },
  },
  workspaceRow: {
    backgroundColor: '#fafafa',
  },
  expandCell: {
    width: 50,
  },
  accountInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusActive: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontWeight: 'bold',
  },
  statusInactive: {
    backgroundColor: '#9E9E9E',
    color: '#fff',
    fontWeight: 'bold',
  },
  emptyWorkspaces: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
}));

const INITIAL_PAGE = 1;
const PAGE_SIZE = 25;

export const WorkspacesTab = ({ environment }: { environment: Environment }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { allowed: canCreate } = usePermission({ permission: openControlWritePermission });

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi],
  );
  const { notification, showError, showSuccess, hideNotification } =
    useNotification();

  const [accounts, setAccounts] = useState<AccountWithWorkspaces[]>([]);
  const [accountsHasNext, setAccountsHasNext] = useState(false);
  const [accountsPage, setAccountsPage] = useState(INITIAL_PAGE);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [accountsFetchTrigger, setAccountsFetchTrigger] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { loading: initialLoading } = useAsync(async () => {
    try {
      const response = await workspacesService.searchAccounts(environment, {
        search: searchQuery || undefined,
        page: INITIAL_PAGE,
        pageSize: PAGE_SIZE,
      });

      const mapped: AccountWithWorkspaces[] = (response.items || []).map(
        account => ({
          ...account,
          workspaces: undefined,
          workspacesLoading: false,
          workspacesLoaded: false,
        }),
      );

      setAccounts(mapped);
      setAccountsPage(INITIAL_PAGE);
      setAccountsHasNext(response.hasNext);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Error fetching accounts',
      );
      setAccounts([]);
    }
  }, [workspacesService, environment, accountsFetchTrigger]);

  const loadMoreAccounts = useCallback(async () => {
    if (!accountsHasNext || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = accountsPage + 1;
      const response = await workspacesService.searchAccounts(environment, {
        search: searchQuery || undefined,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });

      const mapped: AccountWithWorkspaces[] = (response.items || []).map(
        account => ({
          ...account,
          workspaces: undefined,
          workspacesLoading: false,
          workspacesLoaded: false,
        }),
      );

      setAccounts(prev => [...prev, ...mapped]);
      setAccountsPage(nextPage);
      setAccountsHasNext(response.hasNext);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Error fetching accounts',
      );
    } finally {
      setIsLoadingMore(false);
    }
  }, [accountsHasNext, isLoadingMore, accountsPage, workspacesService, environment, searchQuery, showError]);

  const fetchWorkspacesForAccount = async (account: AccountWithWorkspaces) => {
    const accountId = account.id;

    const updateAccountState = (updates: Partial<AccountWithWorkspaces>) => {
      setAccounts(prev =>
        prev.map(acc =>
          acc.id === accountId ? { ...acc, ...updates } : acc,
        ),
      );
    };

    updateAccountState({ workspacesLoading: true });

    try {
      const response = await workspacesService.searchWorkspacesByAccount(
        accountId,
        environment,
      );

      updateAccountState({
        workspaces: response?.items || [],
        workspacesLoading: false,
        workspacesLoaded: true,
      });
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : `Error fetching workspaces for account ${account.name}`,
      );
      updateAccountState({
        workspaces: [],
        workspacesLoading: false,
        workspacesLoaded: true,
      });
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setAccountsFetchTrigger(prev => prev + 1);
  };

  const toggleAccount = (account: AccountWithWorkspaces) => {
    const accountId = account.id;
    const isExpanding = !expandedAccounts.has(accountId);

    setExpandedAccounts(prev => {
      const newExpanded = new Set(prev);

      if (newExpanded.has(accountId)) {
        newExpanded.delete(accountId);
      } else {
        newExpanded.add(accountId);
      }

      return newExpanded;
    });

    if (isExpanding && !account.workspacesLoaded && !account.workspacesLoading) {
      fetchWorkspacesForAccount(account);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAccountsFetchTrigger(prev => prev + 1);
  };

  const handleNewWorkspaceRequest = () => {
    setIsDialogOpen(true);
  };

  const handleViewDetails = (workspace: WorkspaceItem) => {
    navigate(`/workspaces/${environment}/detail/${workspace.id}`, {
      state: { workspace, environment },
    });
  };

  const handleRetryProvision = async (workspace: WorkspaceItem) => {
    try {
      await workspacesService.retryWorkspaceProvision(environment, workspace.id);
      showSuccess('Provisioning retried successfully.');
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Error retrying provisioning',
      );
    }
  };

  const hasActiveFilters = !!searchQuery;

  useEffect(() => {
    setExpandedAccounts(new Set());
  }, [accountsFetchTrigger]);

  const getWorkspaceStatusClass = (status: string | undefined) => {
    if (status === 'ACTIVE') {
      return classes.statusActive;
    } else if (status === 'REJECTED') {
      return classes.statusInactive;
    }
    return undefined;
  };

  return (
    <Box display="flex" flexDirection="column" gridRowGap={16}>
      <Box display="flex" flexDirection="column" gridRowGap={16}>
        <Box className={classes.filterContainer}>
          <form
            onSubmit={handleSearchSubmit}
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 16,
            }}
          >
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
            >
              Search
            </Button>
          </form>

          {hasActiveFilters && (
            <Button
              variant="outlined"
              color="default"
              size="medium"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}

          {canCreate && (
            <Box className={classes.actionButtonsContainer}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleNewWorkspaceRequest}
              >
                New
              </Button>
            </Box>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary">
          Displaying {accounts.length} accounts
        </Typography>

        <Box className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.headerCell} ${classes.expandCell}`}
                />
                <TableCell className={classes.headerCell}>
                  Totvs Code
                </TableCell>
                <TableCell className={classes.headerCell}>Name</TableCell>
                <TableCell className={classes.headerCell}>
                  Subdomain
                </TableCell>
                <TableCell className={classes.headerCell}>Document</TableCell>
                <TableCell className={classes.headerCell}>
                  RAC Tenant
                </TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {initialLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Loading accounts...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!initialLoading && accounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {hasActiveFilters
                        ? 'No account found with the applied filters'
                        : 'No account available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!initialLoading &&
                accounts.map(account => (
                  <React.Fragment key={account.id}>
                    <TableRow className={classes.accountRow}>
                      <TableCell className={classes.expandCell}>
                        <IconButton
                          size="small"
                          onClick={() => toggleAccount(account)}
                        >
                          {expandedAccounts.has(account.id) ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell colSpan={7}>
                        <Box className={classes.accountInfo}>
                          <Typography variant="body1">
                            {account.totvsCode} - {account.name}
                          </Typography>
                          {account.workspacesLoaded && account.workspaces && (
                            <Chip
                              label={`${account.workspaces.length} workspace${account.workspaces.length !== 1 ? 's' : ''
                                }`}
                              size="small"
                              color="primary"
                            />
                          )}
                          {account.workspacesLoading && (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {expandedAccounts.has(account.id) && (
                      <>
                        {account.workspacesLoading && (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Box p={2}>
                                <CircularProgress size={24} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}

                        {account.workspacesLoaded &&
                          account.workspaces?.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <Box className={classes.emptyWorkspaces}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    No workspace found for this account
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}

                        {account.workspacesLoaded &&
                          account.workspaces &&
                          account.workspaces.length > 0 &&
                          account.workspaces.map((workspace, index) => (
                            <TableRow
                              key={workspace.id || `workspace-${index}`}
                              className={classes.workspaceRow}
                            >
                              <TableCell className={classes.expandCell} />
                              <TableCell>
                                {workspace.totvsCode ?? ''}
                              </TableCell>
                              <TableCell>{workspace.name ?? ''}</TableCell>
                              <TableCell>
                                {workspace.subdomain ?? ''}
                              </TableCell>
                              <TableCell>
                                {workspace.document?.value ?? ''}
                              </TableCell>
                              <TableCell>{workspace.racId ?? ''}</TableCell>
                              <TableCell>
                                <Chip
                                  label={workspace.status ?? 'Unknown'}
                                  className={getWorkspaceStatusClass(workspace.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="text"
                                  color="primary"
                                  size="small"
                                  onClick={() => handleViewDetails(workspace)}
                                >
                                  details
                                </Button>
                                {canCreate && ['INACTIVE', 'PENDING', 'REJECTED'].includes(workspace.status ?? '') && (
                                  <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleRetryProvision(workspace)}
                                  >
                                    retry
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {accountsHasNext && !isLoadingMore && !initialLoading && accounts.length > 0 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="outlined" onClick={loadMoreAccounts}>
            Load more accounts
          </Button>
        </Box>
      )}

      {isLoadingMore && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <NewWorkspaceDialog
        open={isDialogOpen}
        environment={environment}
        onClose={() => setIsDialogOpen(false)}
        workspacesService={workspacesService}
        showSuccess={showSuccess}
        showError={showError}
        onCreated={() => setAccountsFetchTrigger(prev => prev + 1)}
      />

      {notification &&
        <NotificationSnackbar
          notification={notification}
          onClose={hideNotification}
        />
      }
    </Box>
  );
};
